import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Search, Hourglass, BadgeCheck, X } from "lucide-react";
import { API } from "@/App";

const formatDateFR = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export default function LookupSection() {
  const [name, setName] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reservations/lookup`, { params: { name } });
      setResults(data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelEmail, setCancelEmail] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const cancel = async (e) => {
    e.preventDefault();
    if (!cancelEmail.trim() || !cancelTarget) return;
    setCancelling(true);
    try {
      const { data } = await axios.post(`${API}/reservations/${cancelTarget.id}/cancel`, { email: cancelEmail.trim() });
      toast.success("Réservation annulée", {
        description: data.notified > 0 ? "L'équipe du restaurant a été prévenue par email." : "Votre réservation a bien été supprimée.",
      });
      setResults((list) => (list || []).filter((x) => x.id !== cancelTarget.id));
      setCancelTarget(null);
      setCancelEmail("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Impossible d'annuler cette réservation.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <section id="statut" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="lookup-section">
      <div className="mx-auto max-w-2xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Suivi</p>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-stone-900 sm:text-4xl">
            Vérifier ma <span className="italic text-burgundy">réservation</span>
          </h2>
          <p className="mt-4 text-base text-stone-600">
            Entrez le nom utilisé lors de votre réservation pour connaître son état de validation en temps réel.
          </p>
        </motion.div>

        <motion.form
          onSubmit={search}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-8 flex flex-col gap-3 sm:flex-row"
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Votre nom (ex : Jean Dupont)"
            className="flex-1 rounded-full border border-stone-200 bg-white px-6 py-4 text-sm outline-none transition-all placeholder:text-stone-400 focus:border-burgundy focus:ring-2 focus:ring-burgundy/15"
            data-testid="lookup-name-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-full bg-burgundy px-8 py-4 text-sm font-semibold text-cream shadow-lg shadow-burgundy/25 transition-all hover:-translate-y-0.5 hover:bg-burgundy-dark disabled:opacity-60"
            data-testid="lookup-submit-button"
          >
            <Search className="h-4 w-4" />
            {loading ? "Recherche…" : "Vérifier"}
          </button>
        </motion.form>

        {results !== null && (
          <div className="mt-8 space-y-3 text-left" data-testid="lookup-results">
            {results.length === 0 && (
              <p className="rounded-2xl border border-stone-200 bg-white p-6 text-center text-sm text-stone-500" data-testid="lookup-empty">
                Aucune réservation trouvée à ce nom. Vérifiez l'orthographe exacte utilisée lors de la réservation.
              </p>
            )}
            {results.map((r) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col gap-3 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                data-testid={`lookup-result-${r.id}`}
              >
                <div>
                  <p className="font-semibold capitalize text-stone-900">{formatDateFR(r.date)}</p>
                  <p className="mt-0.5 text-sm text-stone-500">
                    {r.service} · {r.guests} personne{r.guests > 1 ? "s" : ""} · réservée le{" "}
                    {new Date(r.created_at).toLocaleDateString("fr-FR")}
                  </p>
                </div>
                {r.status === "confirmed" ? (
                  <span className="flex w-fit items-center gap-2 rounded-full bg-sage-light px-4 py-2 text-xs font-semibold text-sage" data-testid={`lookup-status-${r.id}`}>
                    <BadgeCheck className="h-4 w-4" />
                    Réservation confirmée
                  </span>
                ) : (
                  <span className="flex w-fit items-center gap-2 rounded-full bg-gold-light px-4 py-2 text-xs font-semibold text-amber-900" data-testid={`lookup-status-${r.id}`}>
                    <Hourglass className="h-4 w-4" />
                    En attente de confirmation
                  </span>
                )}
                <button
                  onClick={() => { setCancelTarget(r); setCancelEmail(""); }}
                  className="w-fit rounded-full border border-red-200 px-4 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50 sm:w-auto"
                  data-testid={`cancel-reservation-${r.id}`}
                >
                  Annuler
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {cancelTarget && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-burgundy-deep/70 p-4 backdrop-blur-sm"
          onClick={() => setCancelTarget(null)}
          data-testid="cancel-modal"
        >
          <div className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="font-serif text-2xl font-semibold text-stone-900">Annuler la réservation</h3>
              <button
                onClick={() => setCancelTarget(null)}
                aria-label="Fermer"
                className="rounded-full p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700"
                data-testid="cancel-close-button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-2 text-sm capitalize text-stone-600">
              {formatDateFR(cancelTarget.date)} · {cancelTarget.service} · {cancelTarget.guests} personne{cancelTarget.guests > 1 ? "s" : ""}
            </p>
            <p className="mt-3 text-sm text-stone-500">
              Pour confirmer l'annulation, saisissez l'adresse email utilisée lors de la réservation.
              L'équipe du restaurant sera prévenue par email.
            </p>
            <form onSubmit={cancel} className="mt-5 space-y-4">
              <input
                type="email"
                required
                autoFocus
                value={cancelEmail}
                onChange={(e) => setCancelEmail(e.target.value)}
                placeholder="votre@email.fr"
                className="w-full rounded-xl border border-stone-200 bg-cream/60 px-4 py-3 text-sm outline-none transition-all placeholder:text-stone-400 focus:border-burgundy focus:ring-2 focus:ring-burgundy/15"
                data-testid="cancel-email-input"
              />
              <button
                type="submit"
                disabled={cancelling}
                className="w-full rounded-full bg-red-700 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-red-800 disabled:opacity-60"
                data-testid="cancel-confirm-button"
              >
                {cancelling ? "Annulation…" : "Confirmer l'annulation"}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
